import CryptoJS from "crypto-js";

// Share links put AES output in URL path segments with every "/" written as
// "bbbb", and read it back with .replace(/bbbb/g, "/"). Base64 also contains
// "b", so a "b" next to an encoded "/" made some links impossible to decode.

const toLink = (text) => text.replace(/\//g, "bbbb");
const fromLink = (text) => text.replace(/bbbb/g, "/");

// New links: keep only ciphertexts whose "bbbb" form reads back unchanged.
// Passphrase encryption uses a random salt, so a retry gives a new ciphertext.
const encrypt = CryptoJS.AES.encrypt;
CryptoJS.AES.encrypt = function (...args) {
  let result = encrypt.apply(this, args);
  for (let attempt = 0; attempt < 20; attempt++) {
    const text = result.toString();
    if (fromLink(toLink(text)) === text) break;
    result = encrypt.apply(this, args);
  }
  return result;
};

// Every way a run of n b's can be read as literal "b"s and "bbbb" (= "/").
const readingsOfRun = (n) => {
  if (n === 0) return [""];
  const readings = readingsOfRun(n - 1).map((rest) => "b" + rest);
  if (n >= 4) readings.push(...readingsOfRun(n - 4).map((rest) => "/" + rest));
  return readings;
};

// The other possible readings of a segment, apart from the standard one.
const alternativeReadings = (segment) => {
  const runs = [...segment.matchAll(/b{4,}/g)];
  let readings = [""];
  let last = 0;
  for (const run of runs) {
    const between = segment.slice(last, run.index);
    const options = readingsOfRun(run[0].length);
    readings = readings.flatMap((r) => options.map((o) => r + between + o));
    if (readings.length > 512) return [];
    last = run.index + run[0].length;
  }
  const standard = fromLink(segment);
  return readings
    .map((r) => r + segment.slice(last))
    .filter((r) => r !== standard);
};

const decryptText = (text, secretKey) =>
  CryptoJS.AES.decrypt(text, secretKey).toString(CryptoJS.enc.Utf8);

// Decrypts a link segment exactly as before. Only when that gives no usable
// value (links made before the fix above) are the other readings tried.
export const decryptLinkSegment = (segment, secretKey, isUsable, isRecovered) => {
  let value;
  let failure;
  try {
    value = decryptText(fromLink(segment), secretKey);
  } catch (error) {
    failure = error;
  }
  if (!failure && isUsable(value)) return value;

  for (const reading of alternativeReadings(segment)) {
    let text;
    try {
      text = decryptText(reading, secretKey);
    } catch {
      continue;
    }
    if (isRecovered(text)) return text;
  }

  if (failure) throw failure;
  return value;
};
